import React, { useState, useReducer, useContext } from 'react';
import Downshift from 'downshift';
import PropTypes from 'prop-types';
import qs from 'qs';
import { withStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';

import getResponseJson from '../../services/getresponsejson';
import CodeChip from '../codechip/components/codechip';
import { RouteFetcherContext } from '@mk-redbox/route-fetcher';

const styles = theme => ({
  root: {
    position:'relative'
  },
  primaryContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    border: '1px solid black',
    padding:'.8em'
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
    '& label': {
      marginRight:'8px'
    }
  },
  selectedItemContainer: {
    display: 'inline-block',
    marginLeft: '1em'
  },
  optionsContainer: {
    position: 'absolute',
    marginLeft: '62px',
    width: '500px',
    height: '170px',
    overflowY: 'auto',
    border: '1px solid black',
    backgroundColor:'white',
    marginTop:'-19px',
    cursor:'pointer',
    padding:'4px',
    zIndex:'999',
    '& div': {
      borderBottom:'1px solid black'
    }
  }
});

// for server api call param
const API_PARAM_ENCODING_TYPE = {
  DX: 1,
  PROCEDURE: 2
};

// for server api call param
const API_PARAM_ICD_VERSION = {
  ICD10: 10,
  PROCEDURE: 3
};

const API_PARAM_CODE_CATEGORY_CATEGORY = {
  HCC: 1,
  ACA: 2
};

const getResults = (
  term,
  encodingType,
  dos,
  icdVersion,
  codeCategoryCategory,
  backendRoutes
) => {
  const { CmsCodeAutoCompleteWithHccCodes } = backendRoutes;

  const params = {
    term,
    encodingType,
    dos,
    icdVersion,
    category: codeCategoryCategory
  };

  const queryString = qs.stringify(params);

  const url = `${CmsCodeAutoCompleteWithHccCodes}?${queryString}`;

  return fetch(url, {
    credentials: 'include'
  })
  .then(getResponseJson)
  .catch(e => {
    console.error(e.toString());
  });
};

const ACTION_TYPES = {
  CHANGE_INPUT_VALUE: 'CHANGE_INPUT_VALUE'
};

const ACTIONS = {
  changeInputValue: inputValue => ({
    type: ACTION_TYPES.CHANGE_INPUT_VALUE,
    payload: {
      inputValue
    }
  })
};

const autocompleteInitialState = {
  inputValue: ''
};

const autocompleteReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.CHANGE_INPUT_VALUE: {
      const { payload: { inputValue } } = action;
      return {
        ...state,
        inputValue
      };
    }

    default: {
      throw Error('autocompleteReducer should never hit default case');
    }
  }
};

const GeneralAutocomplete = ({
  dateOfService,
  selectedEncoding,
  setSelectedEncoding,
  classes,
  handleDelete = () => {},
  isACA = false
}) => {
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fooState, fooDispatch] = useReducer(
    autocompleteReducer,
    autocompleteInitialState
  );
  const backendRoutes = useContext(RouteFetcherContext);

  const handleInputChange = e => {
    const value = e.target.value;

    if (!value || value.length < 3) {
      setOptions([]);
      return;
    }

    setIsLoading(true);

    // use aca if its aca else use hcc
    const codeCategoryCategoryParam = isACA
      ? API_PARAM_CODE_CATEGORY_CATEGORY.ACA
      : API_PARAM_CODE_CATEGORY_CATEGORY.HCC;

    getResults(
      value,
      API_PARAM_ENCODING_TYPE.DX,
      dateOfService,
      API_PARAM_ICD_VERSION.ICD10,
      codeCategoryCategoryParam,
      backendRoutes
    )
    .then(responseJson => {
      const { result } = responseJson;
      setOptions(result);
      setIsLoading(false);
    })
    .catch(e => {
      console.error(e.toString());
      setIsLoading(false);
    });
  };

  const betterOnInputValueChange = v => {
    const nextVal = (v === undefined || v === null) ? '' : v;
    fooDispatch(ACTIONS.changeInputValue(nextVal));
  };

  return (
    <Downshift
      inputValue={fooState.inputValue}
      onInputValueChange={betterOnInputValueChange}
      selectedItem={selectedEncoding}
      onChange={setSelectedEncoding}
      itemToString={item => (item ? item.code : '')}
      onOuterClick={() => fooDispatch(ACTIONS.changeInputValue(''))}
    >
      {({
        getInputProps,
        getLabelProps,
        isOpen,
        getItemProps,
        highlightedIndex,
        selectedItem
      }) => {
        //const isMultipleEncodings = Array.isArray(selectedEncoding);

        return (
          <div className={classes.root}>
            <div className={classes.primaryContainer}>

              {/* input section */}
              <div className={classes.inputContainer}>
                <label {...getLabelProps()}>Encoding:</label>
                <input
                  {...getInputProps({
                    placeholder: 'Search Encodings',
                    onChange: handleInputChange
                  })}
                />
                {
                  isLoading
                    ? (<CircularProgress variant="indeterminate" size="20px" />)
                    : null
                }
              </div>

              {/* selected item section */}
              <div className={classes.selectedItemContainer}>
                <span>
                  Audited DXes: {
                    selectedEncoding.length > 0 ?
                      selectedEncoding.map(x => (
                        <CodeChip
                          key={x.cmsCodeId}
                          isDelible={true}
                          onDelete={() => handleDelete(x.cmsCodeId, x)}
                          encoding={x}
                        />)) :
                      'NONE'
                  }
                </span>
              </div>

            </div>

            {/* items list section */}
            {
              isOpen && options.length === 1 && options[0].code === null ? (
                <div className={classes.optionsContainer}>
                  <div
                    {...getItemProps({
                        key: null,
                        item: null,
                        index: 0,
                        style: {
                        backgroundColor: 'lightgray',
                        fontWeight: 'bold'
                      }
                    })}
                  >
                    NO MATCHES FOUND
                  </div>
                </div>
              ) : isOpen && options.length > 0 ? (
                <div className={classes.optionsContainer}>
                  {
                    options.map((o, i) => (
                      <div
                        {...getItemProps({
                          key: o.cmsCodeId,
                          item: o,
                          index: i,
                          style: {
                            backgroundColor: highlightedIndex === i ?
                              'lightgray' :
                              'white',
                            fontWeight: selectedItem &&
                              selectedItem.cmsCodeId === o.cmsCodeId ?
                                'bold' :
                                'normal'
                          }
                        })}
                      >
                        {o.display}
                      </div>
                    ))
                  }
                </div>
              ) : null
            }
          </div>
        );
      }}
    </Downshift>
  );
};

GeneralAutocomplete.propTypes = {
  handleDelete: PropTypes.func.isRequired,
  isACA: PropTypes.bool
};

export default withStyles(styles, { withTheme: true })(GeneralAutocomplete);
